import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/api';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    setIsLoggedIn(false);
  }, []);

  // Verificar sesión al cargar (vía httpOnly cookie)
  useEffect(() => {
    const verificarSesion = async () => {
      try {
        const response = await authService.verifySession();
        if (response.success) {
          setUser(response.data.usuario);
          setIsLoggedIn(true);
        }
      } catch (error) {
        // No hay sesión activa, usuario debe loguearse
        console.info('Sesión no encontrada, mostrando login');
      } finally {
        setLoading(false);
      }
    };
    verificarSesion();
  }, []);

  // Interceptor para errores 401 — ya no fuerza logout porque la cookie
  // http-only puede seguir vigente. Solo se registra en consola.
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        const url = error.config?.url || '';
        if (error.response?.status === 401 && !url.startsWith('/auth/')) {
          console.warn('401 recibido en:', url, '— la cookie podría haber expirado');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, []);

  // Login
  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      
      if (response.success) {
        setUser(response.data.usuario);
        setIsLoggedIn(true);
        
        return { success: true };
      }
      return { success: false, error: response.message || 'Credenciales inválidas' };
    } catch (error) {
      console.error('Error en login:', error);
      return { success: false, error: error.message || 'Error al iniciar sesión' };
    }
  };

  const value = {
    user,
    isLoggedIn,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export default AuthContext;
